import React from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import ProductLiveDateFilter from './ProductLiveDateFilter';
import SearchableMultiSelect from './SearchableMultiSelect';
import TouchTooltip from '../../common/TouchTooltip';
import { NumberField } from './ViewDescription';
import {
  BANDS_MAX,
  BANDS_SAVED_NOTE,
  OFF_MAX,
  OFF_MIN,
  PRODUCT_STATUSES,
  SALE_OPTIONS,
  SALE_TOOLTIP,
  SOLD_OUT_AT_OPTIONS,
  SOLD_OUT_AT_TOOLTIP,
} from './constants';
import { bandIndexOf, bandLabels, bucketRange } from '../../../utils/buyerInsightsFormat';

const NO_OPTIONS = [];

// A disabled control keeps its place and says why (spec §0.5: "Not used in Brands you don't carry").
const Why = ({ title, children }) =>
  title ? (
    <Tooltip title={title}>
      <span>{children}</span>
    </Tooltip>
  ) : (
    children
  );

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// The Sold out at select's value: 'all' | 'full_price' | 'discounted' | 'band:<i>' (one of the retailer's buckets).
const soldOutAtValue = (soldOutAt, offRange, bands) => {
  if (soldOutAt !== 'discounted') return soldOutAt;
  const i = bandIndexOf(offRange, bands);
  return i >= 0 ? `band:${i}` : 'discounted';
};

/**
 * The retailer's cut points (sold-out-at spec §2.2, §4): up to three whole percentages, Mixpanel's
 * "Varied" numeric bucket. The resulting bucket labels show live under the fields; Apply (a pause or
 * blur) saves them as the retailer's default.
 */
const BandsField = ({ bands, onApply, isWide }) => {
  const slots = Array.from({ length: BANDS_MAX }, (_, i) => bands[i] ?? null);
  const apply = (i, v) => {
    const next = slots.map((s, j) => (j === i ? v : s)).filter((s) => s !== null);
    const sorted = [...new Set(next)].sort((a, b) => a - b);
    if (sorted.length !== next.length || sorted.some((s, j) => s !== next[j])) return; // not increasing: ignored
    onApply(sorted);
  };
  return (
    <Box sx={{ width: isWide ? 'auto' : '100%' }}>
      <Typography variant="body2" color="text.secondary" component="div" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
        <span>Bands: % off</span>
        {slots.map((s, i) => (
          <NumberField
            key={i}
            value={s}
            onChange={(v) => apply(i, v)}
            min={OFF_MIN}
            max={OFF_MAX}
            allowEmpty
            ariaLabel={`Cut point ${i + 1}`}
          />
        ))}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div">
        {bandLabels(bands).map(cap).join(' · ')}
        {bands.length ? ` — ${BANDS_SAVED_NOTE}` : ' — type one, two or three cut points, e.g. 20 and 40.'}
      </Typography>
    </Box>
  );
};

/**
 * The filter row shared by Products and Brands (spec §0.5): Store · Brand · Category · Product
 * status · Product live date · Sale · Sold out at. `f` is useBuyerInsightsFilters(); `disabledNote`
 * disables Brand, Product status, Product live date, Sale and Sold out at with that tooltip (Brands
 * you don't carry); `soldOutAtNote` greys only Sold out at (views that are not sell-outs).
 * `showStatus` false drops Product status (the Brands tab, Daniel 2026-10-05).
 */
const FilterFields = ({
  f,
  isWide,
  myRetailers = [],
  myRetailerId = '',
  onRetailerChange,
  disabledNote = '',
  soldOutAtNote = '',
  showStatus = true,
}) => {
  const fieldSx = { minWidth: isWide ? 160 : '100%', '& .MuiInputBase-root': { minHeight: isWide ? 40 : 44 } };
  const multiSx = { ...fieldSx, width: isWide ? 220 : '100%', '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' } };
  const off = Boolean(disabledNote);
  const soaOff = off || Boolean(soldOutAtNote);
  const soaNote = disabledNote || soldOutAtNote;
  const bands = f.bands || [];
  const soaValue = soldOutAtValue(f.soldOutAt, f.offRange, bands);

  const onSoldOutAt = (value) => {
    if (value.startsWith('band:')) {
      const i = Number(value.slice(5));
      f.setSoldOutAt('discounted');
      f.setOffRange(bucketRange(i, bands));
      f.filterChanged('sold_out_at', bandLabels(bands)[i]);
      return;
    }
    f.setSoldOutAt(value);
    f.setOffRange(null);
    f.filterChanged('sold_out_at', value);
  };
  const onBands = (next) => {
    // changing a cut point regenerates the buckets; a vanished bucket falls back to Discounted (spec §4)
    if (f.offRange && bandIndexOf(f.offRange, next) < 0) f.setOffRange(null);
    f.applyBands(next);
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
      {myRetailers.length > 1 && (
        <FormControl size="small" sx={fieldSx}>
          <InputLabel id="bi-store" shrink>Store</InputLabel>
          <Select
            labelId="bi-store"
            label="Store"
            value={myRetailerId}
            onChange={(e) => {
              onRetailerChange(e.target.value);
              f.filterChanged('store', e.target.value || 'all');
            }}
            displayEmpty
            notched
            renderValue={(v) => (v ? (myRetailers.find((r) => r.retailer_id === v) || {}).company_name || v : 'All stores')}
          >
            <MenuItem value="">All stores</MenuItem>
            {myRetailers.map((r) => (
              <MenuItem key={r.retailer_id} value={r.retailer_id}>
                {r.company_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <Why title={disabledNote}>
        <SearchableMultiSelect
          id="bi-brand"
          label="Brand"
          allLabel="All brands"
          plural="brands"
          options={f.meta?.brands || NO_OPTIONS}
          value={f.brand}
          onChange={(v) => {
            f.setBrand(v);
            f.filterChanged('brand', v.length);
          }}
          disabled={off}
          sx={multiSx}
        />
      </Why>
      <SearchableMultiSelect
        id="bi-category"
        label="Category"
        allLabel="All categories"
        plural="categories"
        options={f.meta?.categories || NO_OPTIONS}
        value={f.category}
        onChange={(v) => {
          f.setCategory(v);
          f.filterChanged('category', v.length);
        }}
        sx={multiSx}
      />
      {showStatus && (
        <Why title={disabledNote}>
          <FormControl size="small" sx={fieldSx} disabled={off}>
            <InputLabel id="bi-status">Product status</InputLabel>
            <Select
              labelId="bi-status"
              label="Product status"
              value={f.status}
              onChange={(e) => {
                f.setStatus(e.target.value);
                f.filterChanged('status', e.target.value);
              }}
            >
              {PRODUCT_STATUSES.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Why>
      )}
      <Why title={disabledNote}>
        <ProductLiveDateFilter
          value={f.live}
          onChange={(next, which) => {
            f.setLive(next);
            f.filterChanged(which, which === 'live_mode' ? next.mode : next[which === 'live_from' ? 'from' : 'to']);
          }}
          hasInitialImportProducts={Boolean(f.meta?.has_initial_import_products)}
          isWide={isWide}
          disabled={off}
        />
      </Why>
      {/* Sale (sale-filter spec §4): which days count, by the retailer's own price that day. The
          definitions sit on an ⓘ beside the select, like Product live date, so they never cover
          the options (Daniel 2026-10-09). */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: isWide ? 'auto' : '100%' }}>
        <FormControl size="small" sx={fieldSx} disabled={off}>
          <InputLabel id="bi-sale">Sale</InputLabel>
          <Select
            labelId="bi-sale"
            label="Sale"
            value={f.sale}
            onChange={(e) => {
              f.setSale(e.target.value);
              f.filterChanged('sale', e.target.value);
            }}
          >
            {SALE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TouchTooltip title={disabledNote || SALE_TOOLTIP}>
          <InfoOutlined sx={{ fontSize: 18, color: 'text.disabled', cursor: 'help' }} />
        </TouchTooltip>
      </Box>
      {/* Sold out at (sold-out-at spec §4): which SIZES are listed, by the retailer's price on the last
          in-stock day. Discounted splits into the retailer's own bands. A selection rule, not a day mask. */}
      {f.soldOutAt !== undefined && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: isWide ? 'auto' : '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: isWide ? 'auto' : '100%' }}>
            <Why title={soaNote}>
              <FormControl size="small" sx={{ ...fieldSx, minWidth: isWide ? 190 : '100%' }} disabled={soaOff}>
                <InputLabel id="bi-sold-out-at">Sold out at</InputLabel>
                <Select labelId="bi-sold-out-at" label="Sold out at" value={soaValue} onChange={(e) => onSoldOutAt(e.target.value)}>
                  {SOLD_OUT_AT_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                  {bands.length > 0 &&
                    bandLabels(bands).map((label, i) => (
                      <MenuItem key={`band:${i}`} value={`band:${i}`}>{`Discounted: ${label}`}</MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Why>
            <TouchTooltip title={soaNote || SOLD_OUT_AT_TOOLTIP}>
              <InfoOutlined sx={{ fontSize: 18, color: 'text.disabled', cursor: 'help' }} />
            </TouchTooltip>
          </Box>
          {f.soldOutAt === 'discounted' && !soaOff && <BandsField bands={bands} onApply={onBands} isWide={isWide} />}
        </Box>
      )}
    </Box>
  );
};

export default FilterFields;
